import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../components/AuthGate";
import {
  fetchUserProfile,
  fetchUserBets,
  fetchUserSocialPosts,
  followUser,
  unfollowUser,
  checkFollowingUser,
  fetchUserModeration,
  setUserModeration,
  clearUserModeration,
  reportSocialContent,
} from "../api";
import styles from "../styles/UserProfilePage.module.css";
import SocialPostCard from "../components/SocialPostCard";

const fmt = (n, d = 2) => (n == null || Number.isNaN(+n) ? "—" : (+n).toFixed(d));
const asNum = (v) => (Number.isFinite(+v) ? +v : 0);
const isAccaBet = (bet = {}) =>
  (bet.bet_type || "").toLowerCase() === "acca" ||
  bet.is_acca === true ||
  (Array.isArray(bet.acca_legs) && bet.acca_legs.length > 0);
const getDisplayTeams = (bet = {}) => {
  if (bet.teams) return bet.teams;
  if (!isAccaBet(bet)) return "—";
  const fromLegs = (Array.isArray(bet.acca_legs) ? bet.acca_legs : [])
    .map((leg) => leg?.teams)
    .filter(Boolean)
    .join(" / ");
  return fromLegs || "Acca";
};

export default function UserProfilePage() {
  const { identifier } = useParams();
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [bets, setBets] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [expandedAccas, setExpandedAccas] = useState({});
  const [moderation, setModeration] = useState({ blocked: false, muted: false });
  const [showControls, setShowControls] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const [profileData, followStatus] = await Promise.all([
          fetchUserProfile(identifier),
          user
            ? checkFollowingUser(identifier).catch(() => ({ is_following: false }))
            : Promise.resolve({ is_following: false }),
        ]);

        if (!cancelled) {
          setProfile(profileData);
          setIsFollowing(!!(profileData?.is_following ?? followStatus?.is_following));
          if (user && profileData?.id && !profileData?.is_self) {
            fetchUserModeration(profileData.id).then((value) => !cancelled && setModeration(value)).catch(() => {});
          }
        }
      } catch (err) {
        console.error("Error loading profile:", err);
        if (!cancelled) setError(err.response?.data?.detail || "Failed to load user profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [identifier, user]);

  useEffect(() => {
    if (!profile) return;

    let cancelled = false;
    (async () => {
      try {
        const [betsData, postsData] = await Promise.all([
          fetchUserBets(identifier),
          fetchUserSocialPosts(identifier, { limit: 25 }).catch(() => []),
        ]);
        if (!cancelled) {
          setBets(Array.isArray(betsData) ? betsData : []);
          setPosts(Array.isArray(postsData) ? postsData : postsData?.posts || postsData?.items || []);
        }
      } catch (err) {
        console.error("Error loading bets:", err);
        if (!cancelled && err.response?.status === 403) {
          setError("This user's bet tracker is private");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [profile, identifier]);

  const handleFollowToggle = async () => {
    if (!user) {
      alert("Please log in to follow users");
      return;
    }
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await unfollowUser(identifier);
        setIsFollowing(false);
      } else {
        await followUser(identifier);
        setIsFollowing(true);
      }
    } catch (err) {
      console.error("Error toggling follow:", err);
      alert(err.response?.data?.detail || "Failed to update follow status");
    } finally {
      setFollowLoading(false);
    }
  };

  const toggleModeration = async (action) => {
    const enabled = !moderation[action === "block" ? "blocked" : "muted"];
    if (enabled) await setUserModeration(profile.id, action);
    else await clearUserModeration(profile.id, action);
    setModeration((value) => ({ ...value, [action === "block" ? "blocked" : "muted"]: enabled }));
    setShowControls(false);
  };

  const stats = useMemo(() => {
    const totalBets = bets.length;
    const staked = bets.reduce((acc, b) => acc + asNum(b.stake), 0);
    const returned = bets.reduce((acc, b) => {
      const stake = asNum(b.stake);
      const price = asNum(b.price);
      const res = (b.result || "").toUpperCase();
      if (res === "WON") return acc + stake * price;
      if (res === "VOID") return acc + stake;
      return acc;
    }, 0);
    const pnl = returned - staked;
    const roi = staked ? (pnl / staked) * 100 : 0;
    const won = bets.filter((b) => (b.result || "").toUpperCase() === "WON").length;
    const lost = bets.filter((b) => (b.result || "").toUpperCase() === "LOST").length;
    const voided = bets.filter((b) => (b.result || "").toUpperCase() === "VOID").length;
    const pending = bets.filter((b) => !b.result).length;
    const settledCount = won + lost;
    const hitRate = settledCount ? (won / settledCount) * 100 : 0;

    return { totalBets, staked, returned, pnl, roi, won, lost, voided, pending, hitRate };
  }, [bets]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <div className={styles.loadingCard}>Loading profile...</div>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <div className={styles.errorCard}>
            <h2>Error</h2>
            <p>{error}</p>
            <Link to="/" className={styles.backLink}>Back to Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  const isOwnProfile = !!(user && profile && (user.uid === profile.firebase_uid || user.email === profile.email));
  const displayName = profile?.display_name || profile?.username || "User";
  const avatarLetter = displayName[0]?.toUpperCase() || "U";

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.headerCard}>
          <div className={styles.headerIdentity}>
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt={displayName} className={styles.avatarImage} />
            ) : (
              <div className={styles.avatarFallback}>{avatarLetter}</div>
            )}
            <div>
              <h1 className={styles.displayName}>{displayName}</h1>
              {profile?.username ? <p className={styles.handle}>@{profile.username}</p> : null}
              {isOwnProfile ? <p className={styles.ownProfileTag}>This is your profile</p> : null}
            </div>
          </div>

          <div className={styles.headerActions}>
            {!isOwnProfile && user ? (
              <button
                type="button"
                onClick={handleFollowToggle}
                disabled={followLoading}
                className={`${styles.followBtn} ${isFollowing ? styles.following : ""}`}
              >
                {followLoading ? "..." : isFollowing ? "Following" : "Follow"}
              </button>
            ) : null}

            {!isOwnProfile && user ? (
              <div style={{ position: "relative" }}>
                <button type="button" className={styles.editBtn} onClick={() => setShowControls((value) => !value)}>•••</button>
                {showControls ? (
                  <div className={styles.profileMenu}>
                    <button onClick={() => toggleModeration("mute")}>{moderation.muted ? "Unmute" : "Mute"} account</button>
                    <button onClick={() => toggleModeration("block")}>{moderation.blocked ? "Unblock" : "Block"} account</button>
                    <button onClick={() => { reportSocialContent({ target_type: "user", target_id: profile.id, reason: "other" }); setShowControls(false); }}>Report account</button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {isOwnProfile ? (
              <>
                <Link to="/bookmarks" className={styles.editBtn}>Bookmarks</Link>
                <Link to="/account" className={styles.editBtn}>Edit Profile</Link>
              </>
            ) : null}
          </div>
        </div>

        <div className={styles.networkRow}>
          <Link to="/connections?tab=following"><strong>{profile?.following_count || 0}</strong> Following</Link>
          <Link to="/connections?tab=followers"><strong>{profile?.followers_count || 0}</strong> Followers</Link>
          {profile?.bet_stats ? <span><strong>{profile.bet_stats.total_bets || bets.length}</strong> Shared bets</span> : null}
        </div>

        {bets.length > 0 ? (
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Total Bets</span>
              <span className={styles.statValue}>{stats.totalBets}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Win Rate</span>
              <span className={styles.statValue}>{fmt(stats.hitRate, 1)}%</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>ROI</span>
              <span className={`${styles.statValue} ${stats.roi >= 0 ? styles.pos : styles.neg}`}>{fmt(stats.roi, 1)}%</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>P/L</span>
              <span className={`${styles.statValue} ${stats.pnl >= 0 ? styles.pos : styles.neg}`}>
                {stats.pnl >= 0 ? "+" : ""}
                {fmt(stats.pnl)} units
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Record</span>
              <span className={styles.statValue}>
                {stats.won}-{stats.lost}
                {stats.voided > 0 ? <span className={styles.subtle}> ({stats.voided}V)</span> : null}
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Pending</span>
              <span className={styles.statValue}>{stats.pending}</span>
            </div>
          </div>
        ) : null}

        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2>Posts</h2>
            <span>{posts.length} posts</span>
          </div>
          {posts.length ? (
            <div style={{ display: "grid", gap: 12 }}>
              {posts.map((post, idx) => (
                <SocialPostCard key={post.id || post.post_id || idx} post={post} dark />
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>No posts yet</div>
          )}
        </div>

        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2>Bet Tracker</h2>
            <span>{bets.length} picks</span>
          </div>

          {error && bets.length === 0 ? <div className={styles.emptyState}>{error}</div> : null}
          {!error && bets.length === 0 ? <div className={styles.emptyState}>No bets tracked yet</div> : null}

          {bets.length > 0 ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Teams</th>
                    <th>Market</th>
                    <th>Bookmaker</th>
                    <th>Odds</th>
                    <th>Stake</th>
                    <th>Result</th>
                    <th className={styles.num}>Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {bets.map((bet) => {
                    const acca = isAccaBet(bet);
                    const expanded = !!expandedAccas[bet.id];
                    const legs = Array.isArray(bet.acca_legs) ? bet.acca_legs : [];
                    const stake = asNum(bet.stake);
                    const price = asNum(bet.price);
                    const res = (bet.result || "").toUpperCase();
                    let profit = 0;
                    if (res === "WON") profit = stake * (price - 1);
                    else if (res === "LOST") profit = -stake;
                    return (
                      <React.Fragment key={bet.id}>
                        <tr>
                          <td className={styles.dateCell}>{bet.created_at ? new Date(bet.created_at).toLocaleDateString() : "—"}</td>
                          <td>
                            {getDisplayTeams(bet)}
                            {acca ? <span className={styles.accaBadge}>Acca</span> : null}
                          </td>
                          <td>{bet.market || "—"}</td>
                          <td>{bet.bookmaker || "—"}</td>
                          <td>{fmt(price, 2)}</td>
                          <td>{fmt(stake, 2)}</td>
                          <td>
                            <span
                              className={`${styles.resultPill} ${
                                res === "WON" ? styles.resultWon : res === "LOST" ? styles.resultLost : styles.resultPending
                              }`}
                            >
                              {bet.result || "PENDING"}
                            </span>
                          </td>
                          <td className={`${styles.num} ${profit > 0 ? styles.pos : profit < 0 ? styles.neg : ""}`}>
                            {res ? `${profit >= 0 ? "+" : ""}${fmt(profit, 2)}` : "—"}
                          </td>
                        </tr>
                        {acca ? (
                          <tr>
                            <td colSpan="8" className={styles.accaCell}>
                              <button
                                type="button"
                                className={styles.expandBtn}
                                onClick={() =>
                                  setExpandedAccas((prev) => ({ ...prev, [bet.id]: !prev[bet.id] }))
                                }
                              >
                                {expanded ? "Hide acca legs" : "Show acca legs"}
                              </button>
                              {expanded ? (
                                <div className={styles.accaLegList}>
                                  {legs.length ? (
                                    legs.map((leg, idx) => (
                                      <div key={`${bet.id}-leg-${idx}`} className={styles.accaLegItem}>
                                        <span>{idx + 1}.</span>
                                        <span>{leg?.teams || "—"}</span>
                                        <span>{leg?.market || "—"}</span>
                                        <span>{leg?.bookmaker || "—"}</span>
                                        <span>{fmt(leg?.price, 2)}</span>
                                      </div>
                                    ))
                                  ) : (
                                    <div className={styles.subtle}>No leg details available.</div>
                                  )}
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
